import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startPayment, PAYMENT_PURPOSE } from '../../services/paymentService';
import { workbookService } from '../../services/workbookService';
import { getWorkbookPricing } from '../../data/workbookCatalog';
import { useCoins } from '../../context/CoinContext';

import './WorkbookPurchaseModal.css';

const WorkbookPurchaseModal = ({ open, book, userId, onClose, isRenewal }) => {
  const navigate = useNavigate();
  // 🆕 دریافت addCoins از کانتکست برای برگرداندن سکه در صورت خطا
  const { coins, removeCoins, addCoins } = useCoins();

  const [loading, setLoading] = useState(false);
  const [coinLoading, setCoinLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coinSuccess, setCoinSuccess] = useState(false);

  // 🆕 قیمت اختصاصی این کتاب کار (با اطمینان از عددی بودن)
  const pricing = book?.customPricing || getWorkbookPricing(book?.id);
  const coinPrice = Number(pricing.coinPrice) || 0;
  const tomanPrice = Number(pricing.tomanPrice) || 0;
  const durationDays = Number(pricing.durationDays) || 365;

  const hasEnoughCoins = coins >= coinPrice;
  const shortage = Math.max(0, coinPrice - coins);
  const isBusy = loading || coinLoading || coinSuccess;

  // ===== پرداخت با پول واقعی =====
  const handlePurchase = async () => {
    setLoading(true);
    setError(null);
    try {
      await startPayment({
        userId,
        purpose: PAYMENT_PURPOSE.WORKBOOK,
        metadata: {
          bookId: book.id,
        },
        description: `${isRenewal ? 'تمدید' : 'خرید'} کتاب کار: ${book.title}`,
      });
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  // ===== 🆕 پرداخت با سکه =====
  const handleCoinPurchase = async () => {
    if (!hasEnoughCoins) return;
    if (!userId) return setError('لطفاً ابتدا وارد حساب خود شوید.');
    
    setCoinLoading(true);
    setError(null);

    let coinsDeducted = false;

    try {
      // ۱. اگر قیمت بیشتر از صفر بود، سکه کم کن
      if (coinPrice > 0) {
        const removed = removeCoins(coinPrice);
        if (!removed || removed <= 0) {
          throw new Error('لطفاً یک لحظه صبر کنید و دوباره امتحان کنید.');
        }
        coinsDeducted = true;
      }

      // ۲. فعال‌سازی لایسنس ۱ ساله
      await workbookService.activateLicense(
        userId,
        book.id,
        `coin_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        durationDays
      );

      // ۳. نمایش موفقیت و انتقال
      setCoinSuccess(true);
      setTimeout(() => {
        navigate(`/workbook/${book.id}`, { replace: true });
      }, 900);
    } catch (e) {
      // 🔧 اگر سکه کسر شده ولی لایسنس ثبت نشد، سکه را برگردان
      if (coinsDeducted) {
        try {
          addCoins?.(coinPrice);
        } catch {}
      }
      setError(e.message || 'خطا در پرداخت با سکه');
      setCoinLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="workbook-purchase-backdrop" onClick={isBusy ? undefined : onClose}>
      <div className="workbook-purchase-modal" onClick={(e) => e.stopPropagation()}>
        <div className="purchase-header">
          {book.image && <img src={book.image} alt={book.title} className="purchase-cover" />}
          <h3>{book.title}</h3>
          <p className="purchase-desc">
            {isRenewal
              ? 'لایسنس شما منقضی شده است. برای ادامه، تمدید کنید.'
              : 'دسترسی یک‌ساله به تمام محتوای این کتاب کار'}
          </p>
        </div>

        {/* 🆕 دو گزینه‌ی قیمت */}
        <div className="purchase-prices">
          <div className={`price-option ${hasEnoughCoins ? 'is-available' : 'is-disabled'}`}>
            <span className="price-option-label">🪙 با سکه</span>
            <span className="price-option-value">{coinPrice.toLocaleString('fa-IR')}</span>
          </div>
          <div className="price-option">
            <span className="price-option-label">💳 با پول</span>
            <span className="price-option-value">{tomanPrice.toLocaleString('fa-IR')} تومان</span>
          </div>
        </div>

        {/* 🆕 موجودی سکه */}
        <div className="purchase-coin-balance">
          <span>موجودی سکه شما:</span>
          <strong className={hasEnoughCoins ? 'is-enough' : 'is-short'}>
            🪙 {coins.toLocaleString('fa-IR')}
          </strong>
        </div>

        {/* 🆕 هشدار کمبود */}
        {!hasEnoughCoins && coins > 0 && (
          <div className="purchase-coin-shortage">
            ⚠️ {shortage.toLocaleString('fa-IR')} سکه کم دارید — می‌توانید با پول پرداخت کنید
          </div>
        )}

        <div className="purchase-features">
          <span>✅ دسترسی ۱۲ ماهه</span>
          <span>✅ تمام صفحات</span>
          <span>✅ ابزار یادداشت‌برداری</span>
          <span>✅ ذخیره خودکار</span>
        </div>

        {error && <div className="purchase-error">{error}</div>}
        {coinSuccess && (
          <div className="purchase-success">
            ✅ پرداخت با سکه انجام شد! در حال انتقال به کتاب...
          </div>
        )}

        <div className="purchase-actions">
          <button onClick={onClose} disabled={isBusy}>انصراف</button>

          {hasEnoughCoins && (
            <button
              onClick={handleCoinPurchase}
              disabled={isBusy}
              className="purchase-btn purchase-coin-btn"
            >
              {coinLoading ? 'در حال پردازش...' : '🪙 پرداخت با سکه'}
            </button>
          )}

          <button
            onClick={handlePurchase}
            disabled={isBusy}
            className="purchase-btn"
          >
            {loading ? 'در حال انتقال...' : (isRenewal ? 'تمدید با پول' : '💳 پرداخت با پول')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkbookPurchaseModal;